/* ==========================================================================
   PEGBOARD — theme.js
   Custom elements only. No framework, no jQuery. Each element is independent
   so sections can be added or removed without breaking anything else.
   ========================================================================== */

const money = (cents) => {
  const fmt = window.PegboardMoneyFormat || "${{amount}}";
  const amount = (cents / 100).toFixed(2);
  const parts = amount.split(".");
  const withCommas = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return fmt
    .replace(/\{\{\s*amount\s*\}\}/, `${withCommas}.${parts[1]}`)
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${withCommas},${parts[1]}`);
};

const debounce = (fn, wait = 200) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(null, args), wait);
  };
};

/* --- Scroll reveal ------------------------------------------------------ */

const revealObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add("is-inview");
        revealObserver.unobserve(entry.target);
      }
    });
  },
  { rootMargin: "0px 0px -8% 0px", threshold: 0.05 }
);

function bindReveals(root = document) {
  root.querySelectorAll("[data-reveal]:not(.is-inview)").forEach((el) => {
    revealObserver.observe(el);
  });
}

bindReveals();
document.addEventListener("shopify:section:load", (e) => bindReveals(e.target));

/* --- Drawer ------------------------------------------------------------- */

class PegDrawer extends HTMLElement {
  connectedCallback() {
    this.addEventListener("click", (e) => {
      if (e.target.closest("[data-drawer-close]") || e.target.matches(".drawer__scrim")) {
        this.close();
      }
    });
    this._onKey = (e) => {
      if (e.key === "Escape" && this.dataset.open === "true") this.close();
    };
    document.addEventListener("keydown", this._onKey);
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this._onKey);
  }

  open() {
    this.dataset.open = "true";
    document.body.style.overflow = "hidden";
    this._lastFocus = document.activeElement;
    const focusable = this.querySelector(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable) focusable.focus();
  }

  close() {
    this.dataset.open = "false";
    document.body.style.overflow = "";
    if (this._lastFocus) this._lastFocus.focus();
  }
}
customElements.define("peg-drawer", PegDrawer);

document.addEventListener("click", (e) => {
  const trigger = e.target.closest("[data-drawer-open]");
  if (!trigger) return;
  e.preventDefault();
  const target = document.getElementById(trigger.dataset.drawerOpen);
  if (target && typeof target.open === "function") target.open();
});

/* --- Sticky header ------------------------------------------------------ */

class PegHeader extends HTMLElement {
  connectedCallback() {
    if (!this.classList.contains("header--sticky")) return;
    this.lastY = 0;
    this._onScroll = () => {
      const y = window.scrollY;
      this.classList.toggle("header--scrolled", y > 12);
      if (this.dataset.hideOnScroll === "true") {
        const down = y > this.lastY && y > this.offsetHeight * 2;
        this.classList.toggle("header--hidden", down && !this.contains(document.activeElement));
      }
      this.lastY = y;
    };
    window.addEventListener("scroll", this._onScroll, { passive: true });
    this._onScroll();
  }

  disconnectedCallback() {
    window.removeEventListener("scroll", this._onScroll);
  }
}
customElements.define("peg-header", PegHeader);

/* --- Accordion ---------------------------------------------------------- */

class PegAccordion extends HTMLElement {
  connectedCallback() {
    this.single = this.dataset.single === "true";
    this.querySelectorAll(".accordion__trigger").forEach((btn) => {
      btn.addEventListener("click", () => this.toggle(btn));
    });
  }

  toggle(btn) {
    const panel = document.getElementById(btn.getAttribute("aria-controls"));
    const isOpen = btn.getAttribute("aria-expanded") === "true";

    if (this.single && !isOpen) {
      this.querySelectorAll('.accordion__trigger[aria-expanded="true"]').forEach((other) => {
        other.setAttribute("aria-expanded", "false");
        document.getElementById(other.getAttribute("aria-controls")).dataset.open = "false";
      });
    }

    btn.setAttribute("aria-expanded", String(!isOpen));
    panel.dataset.open = String(!isOpen);
  }
}
customElements.define("peg-accordion", PegAccordion);

/* --- Tabs --------------------------------------------------------------- */

class PegTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panels = [...this.querySelectorAll('[role="tabpanel"]')];

    this.tabs.forEach((tab, i) => {
      tab.addEventListener("click", () => this.select(i));
      tab.addEventListener("keydown", (e) => {
        const map = { ArrowRight: 1, ArrowLeft: -1 };
        if (!(e.key in map)) return;
        e.preventDefault();
        const next = (i + map[e.key] + this.tabs.length) % this.tabs.length;
        this.select(next);
        this.tabs[next].focus();
      });
    });
  }

  select(index) {
    this.tabs.forEach((tab, i) => {
      const on = i === index;
      tab.setAttribute("aria-selected", String(on));
      tab.tabIndex = on ? 0 : -1;
      this.panels[i].hidden = !on;
    });
  }
}
customElements.define("peg-tabs", PegTabs);

/* --- Carousel ----------------------------------------------------------- */

class PegCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector(".carousel__track");
    this.prev = this.querySelector("[data-carousel-prev]");
    this.next = this.querySelector("[data-carousel-next]");
    if (!this.track) return;

    this.prev?.addEventListener("click", () => this.scrollBy(-1));
    this.next?.addEventListener("click", () => this.scrollBy(1));
    this.track.addEventListener("scroll", debounce(() => this.update(), 80), {
      passive: true,
    });
    this.update();
  }

  get step() {
    const slide = this.track.querySelector(".carousel__slide");
    if (!slide) return 320;
    const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
    return slide.offsetWidth + gap;
  }

  scrollBy(dir) {
    const perPage = Math.max(1, Math.floor(this.track.clientWidth / this.step));
    this.track.scrollBy({ left: dir * this.step * perPage, behavior: "smooth" });
  }

  update() {
    if (!this.prev || !this.next) return;
    const max = this.track.scrollWidth - this.track.clientWidth - 2;
    this.prev.disabled = this.track.scrollLeft <= 2;
    this.next.disabled = this.track.scrollLeft >= max;
  }
}
customElements.define("peg-carousel", PegCarousel);

/* --- Countdown ---------------------------------------------------------- */

class PegCountdown extends HTMLElement {
  connectedCallback() {
    this.end = new Date(this.dataset.end).getTime();
    if (Number.isNaN(this.end)) return;
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  tick() {
    const diff = this.end - Date.now();
    if (diff <= 0) {
      clearInterval(this.timer);
      if (this.dataset.onEnd === "hide") this.closest("[data-countdown-host]")?.remove();
      this.setValues(0, 0, 0, 0);
      return;
    }
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff / 3600000) % 24);
    const m = Math.floor((diff / 60000) % 60);
    const s = Math.floor((diff / 1000) % 60);
    this.setValues(d, h, m, s);
  }

  setValues(d, h, m, s) {
    const pad = (n) => String(n).padStart(2, "0");
    const set = (key, val) => {
      const el = this.querySelector(`[data-unit="${key}"]`);
      if (el) el.textContent = val;
    };
    set("days", pad(d));
    set("hours", pad(h));
    set("minutes", pad(m));
    set("seconds", pad(s));
  }
}
customElements.define("peg-countdown", PegCountdown);

/* --- Counter (animated number) ------------------------------------------ */

class PegCounter extends HTMLElement {
  connectedCallback() {
    const target = parseFloat(this.dataset.value || "0");
    const dur = parseInt(this.dataset.duration || "1600", 10);
    const valueEl = this.querySelector(".counter__value");
    if (!valueEl) return;

    const suffix = this.dataset.suffix || "";
    const prefix = this.dataset.prefix || "";

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      valueEl.textContent = prefix + target.toLocaleString() + suffix;
      return;
    }

    const io = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) return;
      io.disconnect();
      const start = performance.now();
      const frame = (now) => {
        const p = Math.min(1, (now - start) / dur);
        const eased = 1 - Math.pow(1 - p, 3);
        valueEl.textContent =
          prefix + Math.round(target * eased).toLocaleString() + suffix;
        if (p < 1) requestAnimationFrame(frame);
      };
      requestAnimationFrame(frame);
    });
    io.observe(this);
  }
}
customElements.define("peg-counter", PegCounter);

/* --- Quantity ----------------------------------------------------------- */

class PegQty extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector("input");
    this.querySelector("[data-qty-minus]")?.addEventListener("click", () => this.step(-1));
    this.querySelector("[data-qty-plus]")?.addEventListener("click", () => this.step(1));
  }

  step(dir) {
    const min = parseInt(this.input.min || "1", 10);
    const next = Math.max(min, (parseInt(this.input.value, 10) || min) + dir);
    this.input.value = next;
    this.input.dispatchEvent(new Event("change", { bubbles: true }));
  }
}
customElements.define("peg-qty", PegQty);

/* --- Cart --------------------------------------------------------------- */

const Cart = {
  async get() {
    const res = await fetch(`${window.Shopify?.routes?.root || "/"}cart.js`);
    return res.json();
  },

  async add(items) {
    const res = await fetch(`${window.Shopify?.routes?.root || "/"}cart/add.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.description || err.message || "Could not add to cart");
    }
    return res.json();
  },

  async change(key, quantity) {
    const res = await fetch(`${window.Shopify?.routes?.root || "/"}cart/change.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: key, quantity }),
    });
    return res.json();
  },

  broadcast(cart) {
    document.dispatchEvent(new CustomEvent("peg:cart:updated", { detail: cart }));
  },
};

window.PegboardCart = Cart;

/* --- Cart drawer -------------------------------------------------------- */

class PegCartDrawer extends PegDrawer {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener("peg:cart:updated", (e) => this.render(e.detail));

    this.addEventListener("click", async (e) => {
      const remove = e.target.closest("[data-line-remove]");
      if (remove) {
        e.preventDefault();
        this.setBusy(true);
        const cart = await Cart.change(remove.dataset.lineRemove, 0);
        Cart.broadcast(cart);
        this.setBusy(false);
      }
    });

    this.addEventListener("change", async (e) => {
      const input = e.target.closest("[data-line-qty]");
      if (!input) return;
      this.setBusy(true);
      const cart = await Cart.change(input.dataset.lineQty, parseInt(input.value, 10));
      Cart.broadcast(cart);
      this.setBusy(false);
    });
  }

  setBusy(state) {
    this.querySelector(".drawer__body")?.style.setProperty("opacity", state ? "0.5" : "1");
  }

  async render(cart) {
    const res = await fetch(
      `${window.Shopify?.routes?.root || "/"}?section_id=cart-drawer`
    );
    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, "text/html");
    const fresh = doc.querySelector("peg-cart-drawer");
    if (fresh) this.innerHTML = fresh.innerHTML;

    document.querySelectorAll("[data-cart-count]").forEach((el) => {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });
  }
}
customElements.define("peg-cart-drawer", PegCartDrawer);

/* --- Add to cart form --------------------------------------------------- */

class PegAddToCart extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector("form");
    this.button = this.querySelector('[type="submit"]');
    this.errorBox = this.querySelector("[data-atc-error]");
    if (!this.form) return;

    this.form.addEventListener("submit", async (e) => {
      e.preventDefault();
      this.setLoading(true);
      if (this.errorBox) this.errorBox.textContent = "";

      const data = new FormData(this.form);
      const items = [
        {
          id: parseInt(data.get("id"), 10),
          quantity: parseInt(data.get("quantity") || "1", 10),
        },
      ];

      try {
        await Cart.add(items);
        const cart = await Cart.get();
        Cart.broadcast(cart);
        if (this.dataset.openDrawer !== "false") {
          document.getElementById("cart-drawer")?.open();
        }
      } catch (err) {
        if (this.errorBox) this.errorBox.textContent = err.message;
      } finally {
        this.setLoading(false);
      }
    });
  }

  setLoading(state) {
    if (!this.button) return;
    this.button.setAttribute("aria-busy", String(state));
    this.button.disabled = state;
  }
}
customElements.define("peg-add-to-cart", PegAddToCart);

/* --- Variant picker ----------------------------------------------------- */

class PegVariants extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector("[data-variant-json]").textContent);
    this.selected = [...(this.dataset.initial || "").split("||")];

    this.querySelectorAll(".option-pill, .swatch").forEach((btn) => {
      btn.addEventListener("click", () => {
        const index = parseInt(btn.dataset.optionIndex, 10);
        this.selected[index] = btn.dataset.optionValue;

        this.querySelectorAll(`[data-option-index="${index}"]`).forEach((sib) => {
          sib.setAttribute("aria-pressed", String(sib === btn));
        });

        this.update();
      });
    });

    this.update();
  }

  get current() {
    return this.variants.find((v) =>
      v.options.every((opt, i) => opt === this.selected[i])
    );
  }

  update() {
    const variant = this.current;
    const idInput = document.querySelector("[data-variant-id]");
    const priceEl = document.querySelector("[data-variant-price]");
    const compareEl = document.querySelector("[data-variant-compare]");
    const submit = document.querySelector("[data-atc-button]");

    // Mark unavailable combinations so shoppers see dead ends before clicking
    this.querySelectorAll("[data-option-index]").forEach((btn) => {
      const i = parseInt(btn.dataset.optionIndex, 10);
      const test = [...this.selected];
      test[i] = btn.dataset.optionValue;
      const match = this.variants.find((v) =>
        v.options.every((opt, idx) => opt === test[idx])
      );
      btn.dataset.unavailable = String(!match || !match.available);
    });

    if (!variant) {
      if (submit) {
        submit.disabled = true;
        submit.textContent = submit.dataset.unavailableText || "Unavailable";
      }
      return;
    }

    if (idInput) idInput.value = variant.id;
    if (priceEl) priceEl.textContent = money(variant.price);
    if (compareEl) {
      const show = variant.compare_at_price > variant.price;
      compareEl.hidden = !show;
      if (show) compareEl.textContent = money(variant.compare_at_price);
    }
    if (submit) {
      submit.disabled = !variant.available;
      submit.textContent = variant.available
        ? submit.dataset.addText || "Add to cart"
        : submit.dataset.soldOutText || "Sold out";
    }

    const url = new URL(window.location);
    url.searchParams.set("variant", variant.id);
    window.history.replaceState({}, "", url);

    document.dispatchEvent(
      new CustomEvent("peg:variant:change", { detail: variant })
    );
  }
}
customElements.define("peg-variants", PegVariants);

/* --- Product gallery ---------------------------------------------------- */

class PegGallery extends HTMLElement {
  connectedCallback() {
    this.main = this.querySelector("[data-gallery-main] img");
    this.thumbnails = [...this.querySelectorAll(".pdp__thumb")];

    this.thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener("click", () => this.selectImage(index));
    });

    this.querySelector("[data-gallery-previous]")?.addEventListener("click", () =>
      this.showAdjacentImage(-1)
    );
    this.querySelector("[data-gallery-next]")?.addEventListener("click", () =>
      this.showAdjacentImage(1)
    );

    this.handleKeydown = (event) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;

      const target = event.target;
      const isTyping =
        target instanceof HTMLElement &&
        (target.matches("input, textarea, select") || target.isContentEditable);
      if (isTyping) return;

      event.preventDefault();
      this.showAdjacentImage(event.key === "ArrowLeft" ? -1 : 1);
    };
    document.addEventListener("keydown", this.handleKeydown);

    document.addEventListener("peg:variant:change", (e) => {
      const mediaId = e.detail.featured_media?.id;
      if (!mediaId) return;
      const match = this.querySelector(`.pdp__thumb[data-media-id="${mediaId}"]`);
      const index = this.thumbnails.indexOf(match);
      if (index >= 0) this.selectImage(index);
    });
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this.handleKeydown);
  }

  get currentIndex() {
    const index = this.thumbnails.findIndex(
      (thumbnail) => thumbnail.getAttribute("aria-current") === "true"
    );
    return index >= 0 ? index : 0;
  }

  selectImage(index) {
    if (!this.thumbnails.length) return;

    const normalizedIndex =
      (index + this.thumbnails.length) % this.thumbnails.length;
    const thumbnail = this.thumbnails[normalizedIndex];

    this.thumbnails.forEach((item) =>
      item.setAttribute("aria-current", String(item === thumbnail))
    );

    if (this.main) {
      this.main.src = thumbnail.dataset.full;
      this.main.srcset = thumbnail.dataset.fullSrcset || "";
      this.main.alt = thumbnail.querySelector("img")?.alt || "";
    }

    thumbnail.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
      block: "nearest",
      inline: "nearest",
    });
  }

  showAdjacentImage(direction) {
    this.selectImage(this.currentIndex + direction);
  }
}
customElements.define("peg-gallery", PegGallery);

/* --- Bundle builder ----------------------------------------------------- */

class PegBundle extends HTMLElement {
  connectedCallback() {
    this.discount = parseFloat(this.dataset.discount || "0");
    this.picks = new Map();

    this.querySelectorAll(".bundle__option").forEach((btn) => {
      btn.addEventListener("click", () => this.pick(btn));
    });

    this.querySelector("[data-bundle-add]")?.addEventListener("click", () => this.addAll());
    this.refresh();
  }

  get steps() {
    return [...this.querySelectorAll(".bundle__step")];
  }

  pick(btn) {
    const step = btn.closest(".bundle__step");
    const stepId = step.dataset.stepId;

    step.querySelectorAll(".bundle__option").forEach((sib) => {
      sib.setAttribute("aria-pressed", String(sib === btn));
    });

    this.picks.set(stepId, {
      id: btn.dataset.variantId,
      title: btn.dataset.title,
      price: parseInt(btn.dataset.price, 10),
    });

    step.dataset.complete = "true";
    this.refresh();
  }

  refresh() {
    const rows = this.querySelector("[data-bundle-rows]");
    const totalEl = this.querySelector("[data-bundle-total]");
    const savedEl = this.querySelector("[data-bundle-saved]");
    const addBtn = this.querySelector("[data-bundle-add]");
    const complete = this.picks.size === this.steps.length;

    if (rows) {
      rows.innerHTML = this.steps
        .map((step) => {
          const pick = this.picks.get(step.dataset.stepId);
          const label = step.dataset.stepLabel || "Item";
          return `<div class="bundle__row"><span class="muted">${label}</span><span>${
            pick ? pick.title : "—"
          }</span></div>`;
        })
        .join("");
    }

    const subtotal = [...this.picks.values()].reduce((sum, p) => sum + p.price, 0);
    const total = Math.round(subtotal * (1 - this.discount / 100));

    if (totalEl) totalEl.textContent = money(total);
    if (savedEl) {
      savedEl.hidden = !this.discount || !complete;
      savedEl.textContent = `You save ${money(subtotal - total)}`;
    }
    if (addBtn) {
      addBtn.disabled = !complete;
      addBtn.textContent = complete
        ? addBtn.dataset.readyText || "Add bundle to cart"
        : `Pick ${this.steps.length - this.picks.size} more`;
    }
  }

  async addAll() {
    const btn = this.querySelector("[data-bundle-add]");
    btn.setAttribute("aria-busy", "true");
    try {
      await Cart.add(
        [...this.picks.values()].map((p) => ({ id: parseInt(p.id, 10), quantity: 1 }))
      );
      const cart = await Cart.get();
      Cart.broadcast(cart);
      document.getElementById("cart-drawer")?.open();
    } catch (err) {
      const box = this.querySelector("[data-bundle-error]");
      if (box) box.textContent = err.message;
    } finally {
      btn.setAttribute("aria-busy", "false");
    }
  }
}
customElements.define("peg-bundle", PegBundle);

/* --- Hotspots ----------------------------------------------------------- */

class PegHotspots extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll(".hotspot").forEach((spot) => {
      spot.addEventListener("click", (e) => {
        if (e.target.closest("a")) return;
        const open = spot.getAttribute("aria-expanded") === "true";
        this.querySelectorAll(".hotspot").forEach((s) =>
          s.setAttribute("aria-expanded", "false")
        );
        spot.setAttribute("aria-expanded", String(!open));
      });
    });
  }
}
customElements.define("peg-hotspots", PegHotspots);

/* --- Popup -------------------------------------------------------------- */

class PegPopup extends HTMLElement {
  connectedCallback() {
    this.key = `peg-popup-${this.dataset.key || "default"}`;
    const delay = parseInt(this.dataset.delay || "5", 10) * 1000;
    const days = parseInt(this.dataset.frequency || "7", 10);

    const seen = localStorage.getItem(this.key);
    if (seen && Date.now() - parseInt(seen, 10) < days * 86400000) return;
    if (window.Shopify?.designMode) return;

    setTimeout(() => this.show(), delay);

    this.addEventListener("click", (e) => {
      if (e.target.closest("[data-popup-close]") || e.target.matches(".drawer__scrim")) {
        this.hide();
      }
    });
  }

  show() {
    this.dataset.open = "true";
  }

  hide() {
    this.dataset.open = "false";
    localStorage.setItem(this.key, String(Date.now()));
  }
}
customElements.define("peg-popup", PegPopup);

/* --- Predictive search -------------------------------------------------- */

class PegSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector("[data-search-results]");
    if (!this.input) return;

    this.input.addEventListener(
      "input",
      debounce(async () => {
        const q = this.input.value.trim();
        if (q.length < 2) {
          this.results.innerHTML = "";
          return;
        }
        const url = `${window.Shopify?.routes?.root || "/"}search/suggest?q=${encodeURIComponent(
          q
        )}&resources[type]=product,collection,article&resources[limit]=6&section_id=predictive-search`;
        const res = await fetch(url);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, "text/html");
        this.results.innerHTML = doc.querySelector("[data-search-results]")?.innerHTML || "";
      }, 260)
    );
  }
}
customElements.define("peg-search", PegSearch);

/* --- Collection filters ------------------------------------------------- */

class PegFacets extends HTMLElement {
  connectedCallback() {
    this.addEventListener("change", () => this.apply());
    this.addEventListener("click", (e) => {
      const clear = e.target.closest("[data-facet-clear]");
      if (clear) {
        e.preventDefault();
        this.navigate(clear.href);
      }
    });
  }

  apply() {
    const form = this.querySelector("form");
    const params = new URLSearchParams(new FormData(form)).toString();
    this.navigate(`${window.location.pathname}?${params}`);
  }

  async navigate(url) {
    const grid = document.querySelector("[data-collection-grid]");
    if (grid) grid.style.opacity = "0.45";

    const res = await fetch(url);
    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, "text/html");

    const freshGrid = doc.querySelector("[data-collection-grid]");
    const freshFacets = doc.querySelector("peg-facets");

    if (freshGrid && grid) grid.innerHTML = freshGrid.innerHTML;
    if (freshFacets) this.innerHTML = freshFacets.innerHTML;

    if (grid) grid.style.opacity = "1";
    window.history.pushState({}, "", url);
    bindReveals(document);
  }
}
customElements.define("peg-facets", PegFacets);

/* --- Marquee duplication ------------------------------------------------ */

class PegMarquee extends HTMLElement {
  connectedCallback() {
    const group = this.querySelector(".marquee__group");
    if (!group) return;
    const clone = group.cloneNode(true);
    clone.setAttribute("aria-hidden", "true");
    this.querySelector(".marquee").appendChild(clone);
  }
}
customElements.define("peg-marquee", PegMarquee);

/* --- Theme editor support ----------------------------------------------- */

document.addEventListener("shopify:block:select", (e) => {
  const slide = e.target.closest(".carousel__slide");
  slide?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });

  const trigger = e.target.querySelector(".accordion__trigger");
  if (trigger && trigger.getAttribute("aria-expanded") === "false") trigger.click();
});

document.addEventListener("shopify:section:select", (e) => {
  const drawer = e.target.querySelector("peg-drawer, peg-cart-drawer");
  drawer?.open?.();
  const popup = e.target.querySelector("peg-popup");
  popup?.show?.();
});

document.addEventListener("shopify:section:deselect", (e) => {
  const drawer = e.target.querySelector("peg-drawer, peg-cart-drawer");
  drawer?.close?.();
  const popup = e.target.querySelector("peg-popup");
  if (popup) popup.dataset.open = "false";
});

/* --- Language selector ------------------------------------------------- */

document.addEventListener("change", (event) => {
  const selector = event.target.closest("[data-language-selector]");
  if (!selector) return;

  const isLocalThemePreview = ["127.0.0.1", "localhost", "::1"].includes(window.location.hostname);
  const selectedOption = selector.options[selector.selectedIndex];
  const targetLocaleRoot = selectedOption?.dataset.localeRoot;

  /*
   * Shopify CLI can reject POST /localization on its localhost proxy. Locale
   * root URLs are normal GET routes, so use them only in the local preview.
   * The hosted storefront keeps Shopify's standard localization form, which
   * also persists the shopper's language preference.
   */
  if (isLocalThemePreview && targetLocaleRoot) {
    try {
      window.sessionStorage.setItem("tks-local-preview-locale", selector.value);
    } catch (error) {
      // The URL switch still works when session storage is unavailable.
    }

    const currentLocaleRoot = selector.dataset.currentLocaleRoot || "/";
    let pagePath = window.location.pathname;

    if (
      currentLocaleRoot !== "/" &&
      (pagePath === currentLocaleRoot || pagePath.startsWith(`${currentLocaleRoot}/`))
    ) {
      pagePath = pagePath.slice(currentLocaleRoot.length) || "/";
    }

    const targetPath = targetLocaleRoot === "/"
      ? pagePath
      : `${targetLocaleRoot}${pagePath === "/" ? "" : pagePath}`;
    const targetUrl = new URL(window.location.href);
    targetUrl.pathname = targetPath || "/";
    window.location.assign(targetUrl.toString());
    return;
  }

  selector.form?.submit();
});
